import { TableData } from "../../../custom-components/table";
import t from "../../../stores/language";

export type CodeTypePercentage = {
  codeType: string;
  percentage: number | string;
};

let assetPreviewRows: TableData[] = [];

export const getAssetPreview = () => assetPreviewRows;

export const setAssetPreview = (rows: TableData[]) => {
  assetPreviewRows = rows;
};

export const clearAssetPreview = () => {
  assetPreviewRows = [];
};

const insuranceFlowPaths = ["/insurance/preview", "/insurance/estimates"];

export const isInsuranceFlowPath = (pathname: string) => insuranceFlowPaths.some((path) => pathname === path || pathname.startsWith(`${path}/`));

const shortFailedReason: Record<string, string> = {
  "Code Type already exists with the same percentage": "the same percentage",
  "Code Type already exists with the different percentage": "an invalid percentage",
  "Code Type is duplicated in the sheet": "a duplicate code type",
  "Code Type is required": "a missing code type",
  "Code Type Percentage is required": "an invalid percentage",
  "Code Type Percentage must be a number from 0 to 100": "an invalid percentage",
  "Asset ID is required": "a missing asset ID",
  "Asset ID is duplicated in the sheet": "a duplicate asset ID",
  "Asset Name is required": "a missing asset name",
  "Replacement Cost is required": "a missing replacement cost",
  "Replacement Cost must be a number": "an invalid replacement cost",
  "Code Type is not in Master Insurance Data": "an unknown code type",
};

export const failedReasonSummary = (rows: TableData[]) => {
  const counts = new Map<string, number>();
  rows.forEach((row) => {
    const labels = new Set<string>();
    String(row.reason ?? "")
      .split(". ")
      .forEach((part) => {
        const reason = part.trim();
        if (reason !== "") {
          labels.add(t(shortFailedReason[reason] ?? reason));
        }
      });
    labels.forEach((label) => counts.set(label, (counts.get(label) ?? 0) + 1));
  });
  const phrases = [...counts.entries()]
    .sort((left, right) => right[1] - left[1])
    .map(([reason, count]) => `${count} ${count === 1 ? t("record with") : t("records with")} ${reason}`);
  if (phrases.length < 2) {
    return phrases[0] ?? "";
  }
  if (phrases.length === 2) {
    return `${phrases[0]} and ${phrases[1]}`;
  }
  return `${phrases.slice(0, -1).join(", ")}, and ${phrases[phrases.length - 1]}`;
};

const normalizeHeader = (header: string) =>
  header
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase()
    .replace(/[($)%]/g, "")
    .replace(/\s+/g, " ")
    .trim();

const formatReplacementCost = (value: string) => {
  const [whole, fraction] = value.split(".");
  const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return fraction == null ? `$${withCommas}` : `$${withCommas}.${fraction}`;
};

const parseReplacementCost = (rawCost: string) => {
  const cleanedCost = rawCost.replace(/[$,\s]/g, "");
  if (!/^\d+(\.\d+)?$/.test(cleanedCost) || !Number.isFinite(Number(cleanedCost))) {
    return null;
  }
  return cleanedCost;
};

export const percentageForCodeType = (codeType: string, codeTypes: CodeTypePercentage[]) => {
  const match = codeTypes.find((row) => row.codeType.trim().toLowerCase() === codeType.trim().toLowerCase());
  if (match == null || Number.isNaN(Number(match.percentage))) {
    return "";
  }
  return `${match.percentage}%`;
};

export const buildAssetPreview = (sheetRows: string[][], codeTypes: CodeTypePercentage[]) => {
  const headerRowIndex = sheetRows.findIndex((row) => {
    const headers = row.map((header) => normalizeHeader(header ?? ""));
    return (
      headers.some((header) => header === "asset id") &&
      headers.some((header) => header === "asset name") &&
      headers.some((header) => header === "code type") &&
      headers.some((header) => header === "replacement cost")
    );
  });
  if (headerRowIndex < 0) {
    return { error: "The sheet must have Asset ID, Asset Name, Code Type, and Replacement Cost ($) columns", rows: [] as TableData[] };
  }
  const headers = sheetRows[headerRowIndex].map((header) => normalizeHeader(header ?? ""));
  const assetIdIndex = headers.findIndex((header) => header === "asset id");
  const assetNameIndex = headers.findIndex((header) => header === "asset name");
  const codeTypeIndex = headers.findIndex((header) => header === "code type");
  const costIndex = headers.findIndex((header) => header === "replacement cost");
  const seenAssetIds = new Set<string>();
  const preview: TableData[] = [];
  sheetRows.slice(headerRowIndex + 1).forEach((row) => {
    const assetId = (row[assetIdIndex] ?? "").trim();
    const assetName = (row[assetNameIndex] ?? "").trim();
    const codeType = (row[codeTypeIndex] ?? "").trim();
    const rawCost = (row[costIndex] ?? "").trim();
    if (assetId === "" && assetName === "" && codeType === "" && rawCost === "") {
      return;
    }
    const reasons: string[] = [];
    if (assetId === "") {
      reasons.push("Asset ID is required");
    } else if (seenAssetIds.has(assetId.toLowerCase())) {
      reasons.push("Asset ID is duplicated in the sheet");
    }
    if (assetName === "") {
      reasons.push("Asset Name is required");
    }
    if (codeType === "") {
      reasons.push("Code Type is required");
    }
    const replacementCost = rawCost === "" ? null : parseReplacementCost(rawCost);
    if (rawCost === "") {
      reasons.push("Replacement Cost is required");
    } else if (replacementCost == null) {
      reasons.push("Replacement Cost must be a number");
    }
    const restorationPercent = codeType === "" ? "" : percentageForCodeType(codeType, codeTypes);
    if (codeType !== "" && restorationPercent === "") {
      reasons.push("Code Type is not in Master Insurance Data");
    }
    if (assetId !== "") {
      seenAssetIds.add(assetId.toLowerCase());
    }
    preview.push({
      id: preview.length + 1,
      assetId,
      assetName,
      codeType,
      replacementCost: replacementCost == null ? rawCost : formatReplacementCost(replacementCost),
      restorationPercent,
      reason: reasons.join(". "),
    });
  });
  if (preview.length === 0) {
    return { error: "The sheet has no records", rows: preview };
  }
  return { error: "", rows: preview };
};

export const mergeAssets = (current: TableData[], incoming: TableData[]) => {
  const next = current.map((row) => ({ ...row }));
  incoming.forEach((row) => {
    const assetId = String(row.assetId ?? "")
      .trim()
      .toLowerCase();
    const existing = next.find(
      (item) =>
        String(item.assetId ?? "")
          .trim()
          .toLowerCase() === assetId
    );
    if (existing) {
      existing.assetName = row.assetName;
      existing.codeType = row.codeType;
      existing.replacementCost = row.replacementCost;
      existing.restorationPercent = row.restorationPercent;
      return;
    }
    const nextId = next.reduce((maxId, item) => Math.max(maxId, Number(item.id) || 0), 0) + 1;
    next.push({ ...row, id: nextId });
  });
  return next;
};
